// src/components/MessageBubble.jsx
import React from "react";
import DefensiveMarkdown from "./DefensiveMarkdown";
import DynamicSummaryCard from "./DynamicSummaryCard";

// Standard timestamp formatter
const formatTimestamp = (date) =>
  new Date(date).toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  });

// Util: Smart string fallback (for summary fields that might be objects)
function pickSummaryString(message) {
  if (typeof message.summary === "string" && message.summary.trim()) return message.summary;
  if (message.plan?.[0]?.confirmationSummary && typeof message.plan[0].confirmationSummary === "string") return message.plan[0].confirmationSummary;
  if (typeof message.aiReply === "string" && message.aiReply.trim()) return message.aiReply;
  if (message.planObj && typeof message.planObj.lastConfirmationSummary === "string" && message.planObj.lastConfirmationSummary.trim()) return message.planObj.lastConfirmationSummary;
  if (typeof message.plan?.[0]?.summary === "string" && message.plan[0].summary.trim()) return message.plan[0].summary;
  if (typeof message.clarifier === "string" && message.clarifier.trim()) return message.clarifier;
  if (typeof message.msg === "string" && message.msg.trim()) return message.msg;
  if (typeof message.answer === "string" && message.answer.trim()) return message.answer;
  return "Are you sure you want to proceed?";
}

export default function MessageBubble({
  message,
  time,
  handleConfirm,
  onDisambiguationConfirm,
  onDisambiguationReject,
  onChipPrompt, // NEW: callback for chip/AI buttons
}) {
  const isUser = message.role === "user";
  // Unified best message for all assistant bubbles (except confirmation)
  const displayMsg =
    (typeof message.msg === "string" && message.msg.trim())
      ? message.msg
      : message.answer ?? message.clarifier ?? message.aiReply ?? message.reply ?? (typeof message.summary === "string" ? message.summary : "") ?? (message.plan && message.plan[0] && typeof message.plan[0].summary === "string" ? message.plan[0].summary : "") ?? "";

  // --- Ambiguity/Disambiguation ---
  if (message.status === "needs_disambiguation" && message.candidates?.length > 0) {
    return (
      <div className="flex justify-start">
        <div className="max-w-[70%] px-5 py-4 rounded-2xl shadow border bg-white text-gray-900 mb-2">
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs font-semibold">AI Assistant</span>
            <span className="text-[10px] text-gray-400">{formatTimestamp(time)}</span>
          </div>
          <div className="mb-2 font-medium">
            {message.clarifier || "Multiple matches found. Which one do you mean?"}
          </div>
          <ul className="space-y-2">
            {message.candidates.map((candidate, idx) => (
              <li key={idx}>
                <button
                  className="px-3 py-1 rounded border bg-white hover:bg-blue-100 text-xs font-semibold"
                  onClick={() => onDisambiguationConfirm(candidate, message)}
                >
                  {candidate.name || candidate.email || candidate._id}
                  {candidate.email && (
                    <span className="ml-2 text-gray-500 text-xs">({candidate.email})</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-2">
            <button
              className="px-3 py-1 rounded border bg-white text-xs hover:bg-gray-100"
              onClick={onDisambiguationReject}
            >
              None of these
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Confirmation step (Yes/No) ---
  if (message.status === "needs_confirmation") {
    const summaryString = pickSummaryString(message);
    return (
      <div className="flex justify-start">
        <div className="max-w-[70%] px-5 py-4 rounded-2xl shadow border bg-white text-gray-900 mb-2">
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs font-semibold">AI Assistant</span>
            <span className="text-[10px] text-gray-400">{formatTimestamp(time)}</span>
          </div>
          <div className="mt-2 font-medium">
            <DefensiveMarkdown>
              {summaryString}
            </DefensiveMarkdown>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              className="px-3 py-1 rounded bg-blue-600 text-white text-xs hover:bg-blue-700"
              onClick={() => handleConfirm("yes")}
            >
              Yes
            </button>
            <button
              className="px-3 py-1 rounded bg-gray-300 text-gray-700 text-xs hover:bg-gray-400"
              onClick={() => handleConfirm("no")}
            >
              No
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- All assistant bubbles (summary, error, generic, validation, etc) ---
  if (!isUser) {
    return (
      <div className="flex flex-col items-start mb-6">
        {/* Main AI message bubble */}
        <div className="max-w-[70%] px-5 py-4 rounded-2xl shadow border bg-white text-gray-900 mb-2">
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs font-semibold">AI Assistant</span>
            <span className="text-[10px] text-gray-400">{formatTimestamp(time)}</span>
          </div>
          <DefensiveMarkdown>
            {displayMsg ||
              message.error ||
              (Array.isArray(message.errors) &&
                message.errors.map(
                  (err, i) =>
                    <div key={i}>
                      <b>{err.field}:</b> {err.reason}
                    </div>
                )) ||
              "How can I help?"}
          </DefensiveMarkdown>
          {/* Smart AI Buttons: Use callback if present */}
          {Array.isArray(message.buttons) && message.buttons.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {message.buttons.map((btn, idx) => (
                <button
                  key={idx}
                  onClick={() =>
                    typeof btn.prompt === "string" && onChipPrompt
                      ? onChipPrompt(btn.prompt)
                      : btn.onClick && btn.onClick()
                  }
                  className="text-xs px-2 py-1 rounded border bg-white hover:bg-blue-100"
                >
                  {btn.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {/* --- Summary Card directly below the bubble --- */}
        {message.summary?.entity && message.summary?.data && (
          <div className="w-full mt-2">
            <DynamicSummaryCard entity={message.summary.entity} data={message.summary.data} />
          </div>
        )}
      </div>
    );
  }

  // --- User bubble (blue) ---
  return (
    <div className="flex justify-end">
      <div className="max-w-[70%] px-5 py-4 rounded-2xl shadow-sm bg-blue-600 text-white mb-2">
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs font-semibold">You</span>
          <span className="text-[10px] text-gray-300">{formatTimestamp(time)}</span>
        </div>
        {displayMsg}
      </div>
    </div>
  );
}
